/**
 * components/cardeditor/CardEditorModal.jsx — modal shell for the
 * drag/drop card editor.
 *
 * Chunk 3 (Apr 24 2026).
 *
 * Renders as a full-viewport modal. On open:
 *   1. Loads the tenant's saved custom layout (or the template preset)
 *      into the useLayoutEditor hook
 *   2. Renders Toolbar (top) + EditorCanvas (left) + Inspector (right)
 *   3. On save, validates client-side, then PUTs the layout
 *   4. On reset, PUTs null to clear the custom layout
 *
 * The trigger button is exposed as CardEditorTrigger — caller mounts
 * it wherever on the parent page.
 */

import { useEffect, useState } from 'react';
import { useLayoutEditor } from '../../hooks/useLayoutEditor.js';
import { useCardLayout } from '../../hooks/useCardLayout.js';
import {
  buildMinimalLayout,
  buildPremiumLayout,
} from '../../lib/cards/build-runtime-layout.js';
import { validateLayoutForSave } from '../../lib/cards/layout-constraints.js';
import { EditorCanvas } from './EditorCanvas.jsx';
import { Inspector } from './Inspector.jsx';
import { Toolbar } from './Toolbar.jsx';

export function CardEditorModal({ open, onClose, tenant, staff }) {
  const editor = useLayoutEditor();
  const { layout: savedLayout, loading, saveLayout, resetLayout, refresh } = useCardLayout();

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveWarnings, setSaveWarnings] = useState([]);

  // On open: hydrate editor with saved layout OR template preset
  useEffect(() => {
    if (!open) return;
    if (loading) return;

    if (savedLayout) {
      editor.setInitialLayout(savedLayout);
    } else {
      // Seed from the current template preset
      const { layout } = tenant.card_template === 'premium'
        ? buildPremiumLayout(tenant, staff || MOCK_STAFF)
        : buildMinimalLayout(tenant, staff || MOCK_STAFF);
      editor.setInitialLayout(layout);
    }
    setSaveError(null);
    setSaveWarnings([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only re-hydrate on open/savedLayout/loading
  }, [open, savedLayout, loading]);

  // Block body scroll while modal open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  async function handleSave() {
    if (!editor.layout) return;
    setSaveError(null);

    const check = validateLayoutForSave(editor.layout);
    setSaveWarnings(check.warnings);
    if (!check.ok) {
      setSaveError(`Can't save: ${check.errors.join('; ')}`);
      return;
    }

    setSaving(true);
    try {
      await saveLayout(editor.layout);
      editor.markClean();
    } catch (e) {
      setSaveError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!window.confirm(
      'Reset to the template preset? Your custom layout will be discarded.',
    )) return;
    setSaving(true);
    setSaveError(null);
    try {
      await resetLayout();
      // Re-seed the editor with the preset
      const { layout } = tenant.card_template === 'premium'
        ? buildPremiumLayout(tenant, staff || MOCK_STAFF)
        : buildMinimalLayout(tenant, staff || MOCK_STAFF);
      editor.setInitialLayout(layout);
    } catch (e) {
      setSaveError(e?.message || 'Reset failed.');
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    if (editor.dirty) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    onClose?.();
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Card layout editor"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 30, 28, 0.55)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--sp-4)',
      }}
      onMouseDown={(e) => {
        // Click backdrop to close (with dirty-check)
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          width: '100%',
          maxWidth: 1200,
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <Toolbar
          editor={editor}
          saving={saving}
          onSave={handleSave}
          onReset={handleReset}
          onClose={handleClose}
          canReset={!!savedLayout}
        />

        <MobileGuard>
          <div style={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
          }}>
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: 'var(--sp-4)',
              background: 'var(--color-surface-alt, #f9f8f5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {editor.layout && (
                <EditorCanvas
                  editor={editor}
                  tenant={tenant}
                  staff={staff || MOCK_STAFF}
                />
              )}
            </div>
            <Inspector editor={editor} />
          </div>
        </MobileGuard>

        {(saveError || saveWarnings.length > 0) && (
          <div style={{
            padding: 'var(--sp-3) var(--sp-4)',
            borderTop: '1px solid var(--line-soft)',
            background: saveError ? '#fef2f2' : '#fff8e6',
            fontSize: 'var(--fs-sm)',
          }}>
            {saveError && <div role="alert"><strong>{saveError}</strong></div>}
            {saveWarnings.map((w, i) => <div key={i} className="muted">⚠ {w}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Trigger button exposed for caller to place inline ─── */

export function CardEditorTrigger({ onOpen, hasCustomLayout }) {
  return (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={onOpen}
      style={{ fontSize: 'var(--fs-sm)' }}
    >
      {hasCustomLayout ? 'Edit layout' : 'Customise layout'}
    </button>
  );
}

/* ─── Mobile guard — editor is desktop-only v1 ─── */

function MobileGuard({ children }) {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false,
  );

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (narrow) {
    return (
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--sp-6)',
        textAlign: 'center',
      }}>
        <div>
          <h3 style={{ marginTop: 0 }}>Desktop needed</h3>
          <p className="muted">
            The card layout editor needs at least 1024px of width.
            Please open this page on a desktop or widen your window.
          </p>
        </div>
      </div>
    );
  }
  return children;
}

/* ─── Mock staff (same as KonvaCardPreview default) ─── */

const MOCK_STAFF = {
  first_name: 'Dani',
  last_name: 'Morgan',
  role: 'Senior Cleaner',
  photo_url: null,
};
